import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../../../context/useAuth';
import { useSocket } from '../../../context/useSocket';
import {
  fetchMessages,
  syncMessages,
  markConversationRead,
} from '../api/chatApi';
import {
  MESSAGE_STATUS,
  PATIENT_SIDE,
  createOptimisticMessage,
  getStaffDisplayName,
  normalizeServerMessage,
  upsertMessages,
} from '../utils/messageUtils';

const HISTORY_LIMIT = 30;
const SYNC_LIMIT = 100;
const ACK_TIMEOUT = 8000;

const toTime = (value) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

// Sve stanje i akcije za JEDAN otvoren razgovor: istorija, paginacija starijih
// poruka, optimisticko slanje sa reconcile/retry, live `message:new`, i
// popunjavanje "rupe" posle reconnect-a.
const useConversation = ({ conversationId, open }) => {
  const { user } = useAuth();
  const { socket } = useSocket();

  const [messages, setMessages] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [olderLoading, setOlderLoading] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Ogledala stanja za citanje unutar socket callback-ova (bez stale closure).
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Najnoviji server timestamp - granica za sync(after=...) posle reconnect-a.
  const syncAnchorRef = useRef(null);

  const applyServerMessages = useCallback((incoming) => {
    const list = Array.isArray(incoming) ? incoming : [incoming];
    const normalized = list.filter(Boolean).map(normalizeServerMessage);
    if (normalized.length === 0) return;

    setMessages((prev) => upsertMessages(prev, normalized));

    normalized.forEach((message) => {
      if (toTime(message.createdAt) > toTime(syncAnchorRef.current)) {
        syncAnchorRef.current = message.createdAt;
      }
    });
  }, []);

  const markFailed = useCallback((clientMessageId) => {
    setMessages((prev) =>
      prev.map((message) =>
        message.clientMessageId === clientMessageId
          ? { ...message, status: MESSAGE_STATUS.FAILED }
          : message
      )
    );
  }, []);

  // Reset + inicijalno ucitavanje istorije kada se razgovor otvori.
  useEffect(() => {
    if (!open || !conversationId) return undefined;

    let active = true;
    setMessages([]);
    setHasMoreOlder(false);
    setErrorMessage('');
    setHistoryLoading(true);
    syncAnchorRef.current = null;

    (async () => {
      const { data, ok, message } = await fetchMessages(conversationId, {
        limit: HISTORY_LIMIT,
      });
      if (!active) return;

      setHistoryLoading(false);
      if (!ok || !data) {
        setErrorMessage(message || 'Neuspesno ucitavanje poruka.');
        return;
      }

      // Server vraca najnovije prvo -> okreni u hronoloski redosled za prikaz.
      const chronological = [...(data.messages || [])].reverse();
      const normalized = chronological.map(normalizeServerMessage);
      setMessages(normalized);
      setHasMoreOlder((data.messages || []).length === HISTORY_LIMIT);

      const newest = normalized[normalized.length - 1];
      if (newest) syncAnchorRef.current = newest.createdAt;
    })();

    // Oznaci kao procitano (brise "neprocitano" oznaku za apoteku).
    markConversationRead(conversationId);

    return () => {
      active = false;
    };
  }, [open, conversationId]);

  // Ucitavanje starijih poruka (scroll na vrh) preko `before`.
  const loadOlder = useCallback(async () => {
    if (!conversationId || olderLoading || !hasMoreOlder) return;
    const current = messagesRef.current;
    if (current.length === 0) return;

    const before = current[0].createdAt;
    setOlderLoading(true);
    const { data, ok } = await fetchMessages(conversationId, {
      before,
      limit: HISTORY_LIMIT,
    });
    setOlderLoading(false);

    if (!ok || !data) return;

    const older = (data.messages || []).map(normalizeServerMessage);
    setMessages((prev) => upsertMessages(prev, older));
    setHasMoreOlder((data.messages || []).length === HISTORY_LIMIT);
  }, [conversationId, olderLoading, hasMoreOlder]);

  // Emituj (ili ponovo posalji) poruku sa istim clientMessageId (idempotentno).
  const emitSend = useCallback(
    (payloadMessage) => {
      if (!socket) {
        markFailed(payloadMessage.clientMessageId);
        return;
      }

      socket
        .timeout(ACK_TIMEOUT)
        .emit(
          'message:send',
          {
            clientMessageId: payloadMessage.clientMessageId,
            content: payloadMessage.content,
            conversationId,
          },
          (timeoutError, ack) => {
            if (timeoutError) {
              markFailed(payloadMessage.clientMessageId);
              return;
            }
            if (ack?.ok && ack.message) {
              applyServerMessages(ack.message);
              return;
            }
            markFailed(payloadMessage.clientMessageId);
          }
        );
    },
    [socket, conversationId, applyServerMessages, markFailed]
  );

  const sendMessage = useCallback(
    (rawContent) => {
      const content = (rawContent || '').trim();
      if (!content || !conversationId) return;

      const optimistic = createOptimisticMessage({
        clientMessageId: uuidv4(),
        conversationId,
        content,
        senderId: user?._id || null,
        senderName: getStaffDisplayName(user),
      });

      setMessages((prev) => upsertMessages(prev, optimistic));
      emitSend(optimistic);
    },
    [conversationId, user, emitSend]
  );

  const retryMessage = useCallback(
    (clientMessageId) => {
      const target = messagesRef.current.find(
        (message) => message.clientMessageId === clientMessageId
      );
      if (!target) return;

      setMessages((prev) =>
        prev.map((message) =>
          message.clientMessageId === clientMessageId
            ? { ...message, status: MESSAGE_STATUS.PENDING }
            : message
        )
      );
      emitSend(target);
    },
    [emitSend]
  );

  // Join sobe, live poruke, i sync posle (re)connect-a. Join se mora ponoviti
  // na svaki reconnect (server izgubi clanstvo u sobi na disconnect).
  useEffect(() => {
    if (!socket || !open || !conversationId) return undefined;

    const doJoin = () => socket.emit('conversation:join', { conversationId });

    const doSync = async () => {
      const after = syncAnchorRef.current;
      if (!after) return;
      const { data, ok } = await syncMessages(conversationId, {
        after,
        limit: SYNC_LIMIT,
      });
      if (ok && data) applyServerMessages(data.messages || []);
    };

    const handleMessageNew = (message) => {
      if (!message || message.conversationId !== conversationId) return;
      applyServerMessages(message);
      // Razgovor je otvoren i apoteka ga aktivno cita: dolazna (pacijentova)
      // poruka se odmah oznacava kao procitana, pa se ne akumulira neprocitano
      // (badge i red u listi ostaju cisti). Sopstvene poruke se preskacu.
      if (message.senderSide === PATIENT_SIDE) {
        markConversationRead(conversationId);
      }
    };

    // Poruka izmenjena u mestu (npr. akciona kartica prelazi u "answered").
    // Isti MessageView oblik kao message:new; upsert po `id` je zameni.
    const handleMessageUpdated = (message) => {
      if (!message || message.conversationId !== conversationId) return;
      applyServerMessages(message);
    };

    const handleConnect = () => {
      doJoin();
      doSync();
    };

    if (socket.connected) doJoin();
    socket.on('connect', handleConnect);
    socket.on('message:new', handleMessageNew);
    socket.on('message:updated', handleMessageUpdated);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('message:new', handleMessageNew);
      socket.off('message:updated', handleMessageUpdated);
      if (socket.connected) {
        socket.emit('conversation:leave', { conversationId });
      }
    };
  }, [socket, open, conversationId, applyServerMessages]);

  return {
    messages,
    historyLoading,
    olderLoading,
    hasMoreOlder,
    errorMessage,
    loadOlder,
    sendMessage,
    retryMessage,
  };
};

export default useConversation;
